import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { EmptyState, LoadingSpinner } from '@/components/common'
import { getErrorMessage } from '@/lib/api'
import {
  useUpdateWidgetTemplate,
  useWidgetTemplate,
  type WidgetTemplateUpsert,
} from '@/hooks/api/useWidgets'
import { WidgetTemplateEditor } from './components/WidgetTemplateEditor'

export function WidgetTemplateEditPage() {
  const { t } = useTranslation()
  const base = 'resources.widgets'

  const { id } = useParams<{ id: string }>()
  const templateId = id ? parseInt(id, 10) : 0

  const { data: template, isLoading, isError } = useWidgetTemplate(templateId)
  const updateTemplate = useUpdateWidgetTemplate(templateId)

  const handleSave = async (payload: WidgetTemplateUpsert) => {
    try {
      await updateTemplate.mutateAsync(payload)
      toast.success(t(`${base}.editor.saved_toast`))
    } catch (error) {
      toast.error(t(`${base}.editor.save_failed_toast`, { error: getErrorMessage(error) }))
      // The editor reads a resolved save as saved, so a failure must not resolve.
      throw error
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (isError || !template) {
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState
          title={t(`${base}.editor.not_found_title`)}
          description={t(`${base}.editor.not_found_description`)}
        />
      </div>
    )
  }

  // The editor's header rule and preview panel run to the edges of the content
  // area, so the page cancels the layout's padding instead of adding its own.
  return (
    <div className="-m-4 lg:-m-6">
      <WidgetTemplateEditor
        key={template.id}
        template={template}
        onSave={handleSave}
        isSaving={updateTemplate.isPending}
      />
    </div>
  )
}

export default WidgetTemplateEditPage
